import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import { PMDialog } from "../../../utils/PMDialog";
import { PMField } from "../../../utils/PMField";
import { dispatchWithToast } from "../../../Utils";
import ModernCalculationSelector from "../../../utils/Selector/ModernCalculationSelector";
import { createOfferCalculationAsync } from "../../../../slices/offer-calculation";
import { fetchOfferCalculation } from "../../../../api/offer-calculation";
import OfferTemplateWizardDialog from "../offer-template-wizard-dialog";
import { templateNeedsWizard } from "../utils/template-wizard";
import { expandTemplateByUnits } from "../utils/template-expand";
import {
  buildReplaceFromTemplateValues,
  stripItemIdsForCopy,
  stripNestedCalculationIdsForCopy,
  resolveCalculationTypeId,
  resolveCalculationTypeKey,
} from "../utils/apply-calculation-template";
import "../../../../pages/dashboard/offers/calculations/calculation.css";

const CALCULATION_MODES = {
  lines: "lines",
  modules: "modules",
};

const CALCULATION_TYPES = [
  { value: "standard", labelKey: "offer.calculation.type.standard" },
  { value: "product", labelKey: "offer.calculation.type.product" },
  { value: "hours", labelKey: "offer.calculation.type.hours" },
  { value: "car_allowance", labelKey: "offer.calculation.type.car_allowance" },
  { value: "nested", labelKey: "offer.calculation.type.nested" },
];

function buildCreatePayloadFromTemplate(expanded, titleOverride = "") {
  const blank = {
    id: null,
    title: "",
    description: "",
    calculation_type: "standard",
    items: [],
    nested_calculations: [],
    module_enabled: false,
    module_graph: null,
    module_result: null,
  };
  const replaced = buildReplaceFromTemplateValues(blank, expanded);
  return {
    title: (titleOverride || replaced.title || "").trim() || expanded.title || "Mal",
    description: replaced.description || "",
    active: true,
    calculation_type: resolveCalculationTypeKey(expanded, "standard"),
    calculation_type_id: resolveCalculationTypeId(expanded),
    original_calculation_id: expanded.id,
    show_hours: expanded.show_hours,
    show_quantities: expanded.show_quantities,
    show_products: expanded.show_products,
    show_setup_only: expanded.show_setup_only,
    show_quantities_for_offer_item_only:
      expanded.show_quantities_for_offer_item_only,
    include_product_short_description:
      expanded.include_product_short_description,
    module_enabled: Boolean(expanded.module_enabled),
    module_graph: expanded.module_graph,
    module_result: expanded.module_result,
    items: stripItemIdsForCopy(expanded.items),
    nested_calculations: stripNestedCalculationIdsForCopy(
      expanded.nested_calculations
    ),
  };
}

/**
 * Create-first dialog for offer calculations.
 * Collects builder mode, title and type before opening the builder.
 * Optional: create by loading an existing firm template («Bruk mal»).
 */
export default function CalculationCreateDialog({
  open,
  onClose,
  onCreated,
  navigateOnCreate = true,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [templateWizard, setTemplateWizard] = useState(null);
  const creatingFromTemplateRef = useRef(false);
  const draftTitleRef = useRef("");

  const finishCreate = (created) => {
    const createdId = created?.id;
    onCreated?.(created);
    onClose?.();
    setTemplatePickerOpen(false);
    setTemplateWizard(null);
    if (navigateOnCreate && createdId) {
      navigate(`/dashboard/offers/calculations/${createdId}`, {
        replace: true,
      });
    }
  };

  const createFromTemplateCalc = async (calcData) => {
    if (!calcData?.id || creatingFromTemplateRef.current) return;
    creatingFromTemplateRef.current = true;
    try {
      const expanded = expandTemplateByUnits(calcData);
      const payload = buildCreatePayloadFromTemplate(
        expanded,
        draftTitleRef.current
      );
      const result = await dispatch(createOfferCalculationAsync(payload));
      if (
        createOfferCalculationAsync.rejected.match(result) ||
        result.payload?.errors ||
        !result.payload?.id
      ) {
        toast.error(t("offers.tabs.calculation_load_failed"));
        return;
      }
      toast.success(t("offers.tabs.calculation_template_loaded"));
      finishCreate(result.payload);
    } catch (error) {
      console.error("Error creating calculation from template:", error);
      toast.error(t("offers.tabs.calculation_load_failed"));
    } finally {
      creatingFromTemplateRef.current = false;
    }
  };

  const handleTemplateSelect = async (selectedCalculations) => {
    const templateId = selectedCalculations?.[0]?.id;
    if (!templateId) return;
    try {
      const calcData = await fetchOfferCalculation(templateId);
      if (!calcData || calcData.errors || calcData.id == null) {
        toast.error(t("offers.tabs.calculation_load_failed"));
        return;
      }
      setTemplatePickerOpen(false);
      if (templateNeedsWizard(calcData, t)) {
        setTemplateWizard({ calcData });
        return;
      }
      await createFromTemplateCalc(calcData);
    } catch (error) {
      console.error("Error fetching calculation template:", error);
      toast.error(t("offers.tabs.calculation_load_failed"));
    }
  };

  return (
    <>
      <PMDialog
        open={open}
        onClose={onClose}
        size="2xl"
        title={t("offer.calculation.create.dialog_title")}
        closeOnOutsideClick={false}
      >
        <Formik
          enableReinitialize
          initialValues={{
            title: "",
            description: "",
            calculation_mode: CALCULATION_MODES.lines,
            calculation_type: "standard",
            active: true,
            submit: null,
          }}
          validationSchema={Yup.object({
            title: Yup.string()
              .trim()
              .required(t("offer.calculation.create.title_required")),
            calculation_mode: Yup.string()
              .oneOf([CALCULATION_MODES.lines, CALCULATION_MODES.modules])
              .required(),
          })}
          onSubmit={async (values, helpers) => {
            const payload = {
              title: values.title.trim(),
              description: values.description || "",
              active: values.active !== false,
              calculation_type:
                values.calculation_mode === CALCULATION_MODES.lines
                  ? values.calculation_type || "standard"
                  : "standard",
              module_enabled:
                values.calculation_mode === CALCULATION_MODES.modules,
              items: [],
            };

            try {
              await dispatchWithToast(
                dispatch(createOfferCalculationAsync(payload)),
                (data) => {
                  const created = data?.data || data;
                  finishCreate(created);
                },
                null
              );
              helpers.setStatus({ success: true });
            } catch (err) {
              helpers.setStatus({ success: false });
              helpers.setErrors({ submit: err.message });
            } finally {
              helpers.setSubmitting(false);
            }
          }}
        >
          {(formik) => {
            const mode = formik.values.calculation_mode;
            draftTitleRef.current = formik.values.title || "";

            return (
              <Form>
                <p className="mb-5 text-sm text-slate-500">
                  {t("offer.calculation.create.dialog_hint")}
                </p>

                <div className="offer-calc-settings-scope space-y-5">
                  <div className="offer-calculation-mode-picker">
                    <div>
                      <h4>{t("offer.calculation.mode.title")}</h4>
                      <p>{t("offer.calculation.create.mode_subtitle")}</p>
                    </div>
                    <div className="offer-calculation-mode-picker__options">
                      <button
                        className={[
                          "offer-calculation-mode-picker__option",
                          mode === CALCULATION_MODES.lines ? "is-active" : "",
                        ].join(" ")}
                        type="button"
                        onClick={() =>
                          formik.setFieldValue(
                            "calculation_mode",
                            CALCULATION_MODES.lines
                          )
                        }
                      >
                        <strong>
                          {t("offer.calculation.mode.lines_title")}
                        </strong>
                        <span>{t("offer.calculation.mode.lines_hint")}</span>
                      </button>
                      <button
                        className={[
                          "offer-calculation-mode-picker__option",
                          mode === CALCULATION_MODES.modules ? "is-active" : "",
                        ].join(" ")}
                        type="button"
                        onClick={() =>
                          formik.setFieldValue(
                            "calculation_mode",
                            CALCULATION_MODES.modules
                          )
                        }
                      >
                        <strong>
                          {t("offer.calculation.mode.modules_title")}
                        </strong>
                        <span>{t("offer.calculation.mode.modules_hint")}</span>
                      </button>
                    </div>
                  </div>

                  <PMField
                    name="title"
                    type="text"
                    label={t("offer.calculation.create.title_label")}
                    required
                    {...formik}
                  />

                  {mode === CALCULATION_MODES.lines ? (
                    <PMField
                      name="calculation_type"
                      type="select"
                      label={t("offer.calculation.type")}
                      options={CALCULATION_TYPES.map((opt) => ({
                        value: opt.value,
                        label: t(opt.labelKey),
                      }))}
                      {...formik}
                    />
                  ) : null}

                  <PMField
                    name="description"
                    type="text"
                    label={t("offer.calculation.create.description_label")}
                    multiline
                    rows={3}
                    {...formik}
                  />

                  <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3">
                    <p className="text-sm font-medium text-slate-800">
                      {t("offer.calculation.create.use_template")}
                    </p>
                    <p className="mt-1 text-sm text-slate-500">
                      {t("offer.calculation.create.use_template_hint")}
                    </p>
                    <button
                      type="button"
                      className="mt-3 inline-flex justify-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                      onClick={() => setTemplatePickerOpen(true)}
                      disabled={formik.isSubmitting}
                    >
                      {t("offer.calculation.workspace.load_page_template")}
                    </button>
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-end gap-3 border-t border-slate-200 pt-4">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
                    onClick={onClose}
                    disabled={formik.isSubmitting}
                  >
                    {t("offer.calculation.create.cancel")}
                  </button>
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md bg-[#1463C7] px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#0B3A78] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
                    onClick={formik.submitForm}
                    disabled={formik.isSubmitting}
                  >
                    {t("offer.calculation.create.submit")}
                  </button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </PMDialog>

      <Dialog
        open={templatePickerOpen}
        onClose={() => setTemplatePickerOpen(false)}
        maxWidth="md"
        fullWidth
        sx={{ zIndex: 2100 }}
      >
        <DialogTitle>
          {t("offers.tabs.calculation_select_template_replace")}
        </DialogTitle>
        <DialogContent>
          <ModernCalculationSelector
            open={templatePickerOpen}
            selectedCalculations={[]}
            onChange={handleTemplateSelect}
            onClose={() => setTemplatePickerOpen(false)}
            multiple={false}
            showHeader={false}
          />
        </DialogContent>
      </Dialog>

      <OfferTemplateWizardDialog
        open={Boolean(templateWizard)}
        templateCalc={templateWizard?.calcData}
        onClose={() => setTemplateWizard(null)}
        onConfirm={async (answeredCalc) => {
          setTemplateWizard(null);
          if (!answeredCalc) return;
          await createFromTemplateCalc(answeredCalc);
        }}
      />
    </>
  );
}
